/**
 * Settings page — theme, notifications, data export, and account info.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../store/ToastContext';
import { useAuth } from '../hooks/useAuth';
import { usePushSubscription } from '../hooks/usePushSubscription';
import { getMyHousehold, createInvite } from '../api/household';
import { listBoxes } from '../api/boxes';
import type { HouseholdResponse, InviteResponse } from '@shared/types';
import { Spinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { HouseholdOnboardingWizard } from '../components/HouseholdOnboardingWizard';

export function SettingsPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const {
    isSupported: pushSupported,
    isSubscribed,
    subscribe,
    unsubscribe,
    isLoading: pushLoading,
  } = usePushSubscription();

  const [household, setHousehold] = useState<HouseholdResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<InviteResponse | null>(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Theme state
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.classList.contains('dark');
  });

  // Check if onboarding was skipped
  const onboardingSkipped = localStorage.getItem('peekbox_household_onboarding_skipped') === 'true';

  useEffect(() => {
    loadHousehold();
  }, []);

  // Apply theme on mount and when changed
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  async function loadHousehold() {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyHousehold();
      setHousehold(data);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load household';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateInvite() {
    setGeneratingInvite(true);
    try {
      const invite = await createInvite();
      setInviteLink(invite);
      addToast('success', 'Invite link generated');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate invite link';
      addToast('error', errorMsg);
    } finally {
      setGeneratingInvite(false);
    }
  }

  async function handleCopyLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink.inviteUrl);
      addToast('success', 'Invite link copied to clipboard');
    } catch {
      addToast('error', 'Failed to copy link');
    }
  }

  async function handleTogglePushNotifications() {
    try {
      if (isSubscribed) {
        await unsubscribe();
        addToast('success', 'Push notifications disabled');
      } else {
        await subscribe();
        addToast('success', 'Push notifications enabled');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update notifications';
      addToast('error', errorMsg);
    }
  }

  async function handleExportData() {
    setExporting(true);
    try {
      // Fetch all boxes (without items - items would require separate API call per box)
      const result = await listBoxes({ pageSize: 1000 });
      const boxes = result.data;

      // Create export object
      const exportData = {
        exportedAt: new Date().toISOString(),
        household: household?.name,
        boxCount: boxes.length,
        itemCount: 0, // Items not loaded in bulk export
        boxes: boxes,
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `peekbox-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addToast('success', 'Data exported successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to export data';
      addToast('error', errorMsg);
    } finally {
      setExporting(false);
    }
  }

  function handleCompleteOnboarding() {
    localStorage.setItem('peekbox_household_onboarding_complete', 'true');
    localStorage.removeItem('peekbox_household_onboarding_skipped');
    setShowOnboarding(false);
    addToast('success', 'Setup complete!');
  }

  function handleSkipOnboarding() {
    setShowOnboarding(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner />
      </div>
    );
  }

  if (error || !household) {
    return (
      <EmptyState
        title="Failed to load settings"
        description={error ?? 'Please try again'}
        action={{ label: 'Retry', onClick: loadHousehold }}
      />
    );
  }

  return (
    <>
      {showOnboarding && (
        <HouseholdOnboardingWizard
          onComplete={handleCompleteOnboarding}
          onSkip={handleSkipOnboarding}
        />
      )}

      <div className="space-y-6 max-w-2xl">
        {/* Back button */}
        <button
          type="button"
          onClick={() => navigate('/boxes')}
          className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>

        {/* Account Info */}
        <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Account</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-secondary">Email</span>
              <span className="text-sm font-medium text-text-primary">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-secondary">User ID</span>
              <span className="text-sm font-mono text-xs text-text-secondary">
                {user?.id.slice(0, 8)}...
              </span>
            </div>
          </div>
        </div>

        {/* Complete Setup - show only if onboarding was skipped */}
        {onboardingSkipped && (
          <div className="rounded-lg border border-accent/30 bg-accent/5 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-text-primary mb-2">Complete Setup</h2>
            <p className="text-sm text-text-secondary mb-4">
              You skipped the initial setup. Complete it now to add your first box and start
              organizing.
            </p>
            <button
              type="button"
              onClick={() => setShowOnboarding(true)}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover transition-colors flex items-center gap-2"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Complete Household Setup
            </button>
          </div>
        )}

        {/* Appearance */}
        <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Appearance</h2>
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-medium text-text-primary">Theme</div>
              <div className="text-xs text-text-secondary mt-0.5">
                {darkMode ? 'Dark mode' : 'Light mode'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                darkMode ? 'bg-accent' : 'bg-gray-600'
              }`}
              role="switch"
              aria-checked={darkMode}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  darkMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Notifications */}
        {pushSupported && (
          <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Notifications</h2>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium text-text-primary">Push Notifications</div>
                <div className="text-xs text-text-secondary mt-0.5">
                  {isSubscribed ? 'Enabled' : 'Disabled'}
                </div>
              </div>
              <button
                type="button"
                onClick={handleTogglePushNotifications}
                disabled={pushLoading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                  isSubscribed ? 'bg-accent' : 'bg-gray-600'
                }`}
                role="switch"
                aria-checked={isSubscribed}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isSubscribed ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Household Info */}
        <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary mb-4">Household</h2>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Name</span>
                <span className="text-sm font-medium text-text-primary">{household.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Members</span>
                <span className="text-sm font-medium text-text-primary">
                  {household.memberCount}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary">Created</span>
                <span className="text-sm font-medium text-text-primary">
                  {new Date(household.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Data */}
        <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Data</h2>
          <div>
            <p className="text-sm text-text-secondary mb-4">
              Export all your boxes and items as a JSON file for backup or migration.
            </p>
            <button
              type="button"
              onClick={handleExportData}
              disabled={exporting}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {exporting ? 'Exporting...' : 'Export All Data'}
            </button>
          </div>
        </div>

        {/* Sharing */}
        <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary mb-2">Sharing</h2>
            <p className="text-sm text-text-secondary mb-4">
              Invite others to join your household by sharing an invite link.
            </p>

            {!inviteLink ? (
              <button
                type="button"
                onClick={handleGenerateInvite}
                disabled={generatingInvite}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
                {generatingInvite ? 'Generating...' : 'Generate Invite Link'}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border">
                  <input
                    type="text"
                    readOnly
                    value={inviteLink.inviteUrl}
                    className="flex-1 bg-transparent text-sm text-text-primary outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="rounded px-3 py-1.5 text-sm font-medium text-accent hover:bg-accent/10 transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <div className="flex items-start gap-2 text-xs text-text-secondary">
                  <svg
                    className="h-4 w-4 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    This link can be used unlimited times and never expires. Anyone with this link
                    can join your household.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setInviteLink(null)}
                  className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  Generate new link
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
